/**
 * IngredientEncyclopedia — public index page.
 *
 * Route: /food-pulse/encyclopedia
 * Backend: /api/ingredients (search) + /api/ingredients/index/az + /categories
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, BookOpen, Filter, ChevronRight, Loader2, AlertCircle, Beaker } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const API = process.env.REACT_APP_BACKEND_URL;
const GREEN = "#0F3D2E";
const AMBER = "#E4A140";
const CREAM = "#FBF7EF";

const CATEGORY_LABELS = {
  sweetener:        "Sweeteners",
  preservative:     "Preservatives",
  colour:           "Colours",
  color:            "Colours",
  flavor:           "Flavors",
  emulsifier:       "Emulsifiers",
  stabilizer:       "Stabilizers & Thickeners",
  acidity_regulator:"Acidity regulators",
  processing_aid:   "Processing aids",
  fortifier:        "Fortifiers",
  contaminant:      "Contaminants",
};

export default function IngredientEncyclopedia() {
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ]             = useState("");
  const [cat, setCat]         = useState("");
  const [letter, setLetter]   = useState("");
  const [az, setAz]           = useState([]);
  const [cats, setCats]       = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/ingredients/index/az`)
      .then((r) => setAz(r.data.letters || []))
      .catch(() => setAz([]));
    axios.get(`${API}/api/ingredients/categories`)
      .then((r) => setCats(r.data.categories || []))
      .catch(() => setCats([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 100 };
    if (q)      params.q = q;
    if (cat)    params.category = cat;
    if (letter) params.letter = letter;
    axios.get(`${API}/api/ingredients`, { params })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [q, cat, letter]);

  const grouped = useMemo(() => {
    const g = {};
    for (const it of items) {
      const k = (it.name || "?")[0]?.toUpperCase() || "?";
      (g[k] = g[k] || []).push(it);
    }
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: GREEN }} data-testid="ingredient-encyclopedia-page">
      <SeoHead section="food-pulse"
               title="Food Ingredient Encyclopedia · FoodPulse"
               description="Every additive, preservative, colour and sweetener — one plain-English encyclopedia entry per ingredient. Evidence-based, cross-linked, updated." />

      {/* Hero */}
      <section className="border-b" style={{ borderColor: `${AMBER}55` }}>
        <div className="max-w-6xl mx-auto px-4 py-14 sm:py-16">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest mb-3" style={{ color: AMBER }}>
            <Beaker className="w-3.5 h-3.5" /> FoodPulse · Encyclopedia
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight" data-testid="ency-title">
            Every ingredient, explained.
          </h1>
          <p className="mt-3 max-w-2xl text-base sm:text-lg opacity-80">
            A living encyclopedia of the additives, preservatives, colours, sweeteners
            and emulsifiers hiding in modern food. Written for shoppers, backed by
            FDA, EFSA, NIH and peer-reviewed sources.
          </p>
          <div className="mt-6 max-w-xl relative">
            <Search className="w-4 h-4 absolute left-3 top-3.5 opacity-40" />
            <input value={q} onChange={(e) => { setQ(e.target.value); setLetter(""); }}
                   placeholder="Search ingredient, additive, E-number, alt name..."
                   className="w-full rounded-full border pl-9 pr-4 py-3 text-sm outline-none focus:ring-2"
                   style={{ borderColor: `${GREEN}33`, background: "#fff" }}
                   data-testid="ency-search" />
          </div>
          <div className="mt-3 text-xs opacity-60" data-testid="ency-total">
            {loading ? "loading..." : `${total.toLocaleString()} ingredients indexed`}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-10 grid gap-8 lg:grid-cols-[220px_1fr]">
        {/* Sidebar filters */}
        <aside className="space-y-6">
          {cats.length > 0 && (
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest mb-2 opacity-60">Categories</div>
              <div className="flex flex-col gap-1" data-testid="ency-cat-list">
                <button onClick={() => setCat("")}
                        className={`text-left text-sm rounded px-2 py-1 hover:bg-black/5 ${!cat ? "font-semibold" : ""}`}
                        data-testid="ency-cat-all">
                  All ingredients <span className="opacity-50">({total})</span>
                </button>
                {cats.map((c) => (
                  <button key={c.category} onClick={() => setCat(c.category === cat ? "" : c.category)}
                          className={`text-left text-sm rounded px-2 py-1 hover:bg-black/5 ${cat === c.category ? "font-semibold" : ""}`}
                          data-testid={`ency-cat-${c.category}`}>
                    {CATEGORY_LABELS[c.category] || c.category} <span className="opacity-50">({c.count})</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {az.length > 0 && (
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest mb-2 opacity-60">A–Z index</div>
              <div className="flex flex-wrap gap-1" data-testid="ency-az">
                <button onClick={() => setLetter("")}
                        className={`text-xs font-mono px-2 py-1 rounded ${!letter ? "font-bold underline" : "hover:bg-black/5"}`}>
                  All
                </button>
                {az.map((L) => (
                  <button key={L.letter} onClick={() => setLetter(L.letter === letter ? "" : L.letter)}
                          className={`text-xs font-mono px-2 py-1 rounded ${letter === L.letter ? "font-bold underline" : "hover:bg-black/5"}`}
                          data-testid={`ency-letter-${L.letter}`}>
                    {L.letter}
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* Main list */}
        <main data-testid="ency-list">
          {loading && (
            <div className="flex items-center justify-center py-16 opacity-40" data-testid="ency-loading">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="rounded-2xl border border-dashed p-10 text-center opacity-70"
                 style={{ borderColor: `${AMBER}55` }} data-testid="ency-empty">
              <AlertCircle className="w-10 h-10 mx-auto opacity-40 mb-3" />
              <div className="font-display text-xl mb-1">No ingredient entries yet</div>
              <div className="text-sm">The encyclopedia is being generated — check back in a few minutes.</div>
            </div>
          )}
          {!loading && grouped.map(([letter, list]) => (
            <section key={letter} className="mb-8" data-testid={`ency-group-${letter}`}>
              <div className="font-display text-2xl mb-3 border-b pb-2" style={{ borderColor: `${AMBER}55`, color: AMBER }}>
                {letter}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {list.map((ing) => (
                  <Link key={ing.slug} to={`/food-pulse/encyclopedia/${ing.slug}`}
                        className="rounded-xl border bg-white p-4 hover:-translate-y-0.5 hover:shadow-md transition-all flex items-start gap-3"
                        style={{ borderColor: `${AMBER}55` }}
                        data-testid={`ency-item-${ing.slug}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg" style={{ color: GREEN }}>{ing.name}</div>
                      {(ing.e_number || ing.category) && (
                        <div className="text-[10px] font-mono uppercase tracking-widest mt-0.5" style={{ color: AMBER }}>
                          {ing.e_number ? `${ing.e_number} · ` : ""}
                          {ing.category ? (CATEGORY_LABELS[ing.category] || ing.category) : ""}
                        </div>
                      )}
                      {ing.summary && (
                        <div className="text-xs mt-2 opacity-80 line-clamp-3"
                             dangerouslySetInnerHTML={{
                               __html: (ing.summary || "").replace(/<[^>]+>/g, " ").slice(0, 220) + "..."
                             }} />
                      )}
                      {(ing.alternative_names || []).length > 0 && (
                        <div className="text-[10px] opacity-50 mt-1 line-clamp-1">
                          Also: {ing.alternative_names.slice(0, 3).join(" · ")}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 opacity-40 mt-1 flex-none" />
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </main>
      </div>
    </div>
  );
}
